import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

interface SplitSectionProps {
  id?: string;
  imageSrc: string;
  imageAlt: string;
  headline: string;
  body: string;
  cta: string;
  ctaHref?: string;
  zIndex: number;
}

export default function SplitSection({
  id,
  imageSrc,
  imageAlt,
  headline,
  body,
  cta,
  ctaHref = '#',
  zIndex
}: SplitSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(section);

    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Calculate scroll progress
      if (rect.top < windowHeight && rect.bottom > 0) {
        const progress = Math.min(1, Math.max(0, (windowHeight - rect.top) / (windowHeight + rect.height)));
        setScrollProgress(progress);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Calculate animations based on scroll progress
  const entranceProgress = Math.min(1, scrollProgress * 3); // Entrance: 0-33%
  const exitProgress = Math.max(0, (scrollProgress - 0.6) * 2.5); // Exit: 60-100%

  const imageX = (1 - entranceProgress) * -60 + exitProgress * -18;
  const panelX = (1 - entranceProgress) * 44 + exitProgress * 10;
  const contentOpacity = entranceProgress * (1 - exitProgress);
  const contentY = (1 - entranceProgress) * 10 - exitProgress * 8;

  const copyContent = (
    <>
      <h2 className={`vyera-heading-lg text-[var(--vyera-text-primary)] mb-6 md:mb-8 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        {headline}
      </h2>
      <p className={`vyera-body text-[var(--vyera-text-secondary)] mb-8 md:mb-10 transition-all duration-700 delay-100 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        {body}
      </p>
      {ctaHref.startsWith('/') ? (
        <Link to={ctaHref} className={`vyera-cta transition-all duration-700 delay-200 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {cta}
        </Link>
      ) : (
        <a href={ctaHref} className={`vyera-cta transition-all duration-700 delay-200 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {cta}
        </a>
      )}
    </>
  );

  return (
    <section ref={sectionRef} id={id} className="relative w-full min-h-screen flex overflow-hidden" style={{ zIndex }}>
      {/* Mobile: stacked — image full width, then copy */}
      <div className="flex flex-col w-full md:hidden">
        <div className="w-full aspect-[4/5] flex-shrink-0 overflow-hidden">
          <img src={imageSrc} alt={imageAlt} className="vyera-img-panel" />
        </div>
        <div className="flex-1 min-h-[40vh] bg-[var(--vyera-cream)] flex items-center px-6 py-10 pb-safe">
          <div className="w-full max-w-md">{copyContent}</div>
        </div>
      </div>

      {/* Desktop: split panels with scroll animation */}
      <div className="hidden md:block absolute inset-0">
        <div
          className="absolute left-0 top-0 w-[56vw] h-full"
          style={{ transform: `translateX(${imageX}vw) scale(${1 + (1 - entranceProgress) * 0.08})`, opacity: contentOpacity }}
        >
          <img src={imageSrc} alt={imageAlt} className="vyera-img-panel" />
        </div>
        <div
          className="absolute right-0 top-0 w-[44vw] h-full bg-[var(--vyera-cream)] flex items-center"
          style={{ transform: `translateX(${panelX}vw)`, opacity: contentOpacity }}
        >
          <div className="px-[6vw] max-w-[30vw]" style={{ transform: `translateY(${contentY}vh)`, opacity: contentOpacity }}>
            {copyContent}
          </div>
        </div>
      </div>
    </section>
  );
}
